import { makeAutoObservable, action, computed } from "mobx";
import { subscribe } from "../utils/socket";

export default class DashboardStore {
  users = [];
  orgName = "";
  orgId = null;
  subscriptions = [];

  heartRate = {};
  lastHeartRateCount = 0;
  metrics = {};
  lastMetricsCount = 0;
  userStats = {};

  userSnapshot = {};

  //admin
  adminMode = false;

  // new stores
  filteredHeartRates = [];
  filteredRRIntervals = [];
  filteredSdnn = [];
  filteredStatsData = [];
  filteredStatus = [];
  isRealtime = false;

  green = {};
  yellow = {};
  red = {};

  filterStartDate = "";
  filterEndDate = "";

  setUsers(users) {
    this.users = users;
  }

  setOrgName(orgName) {
    this.orgName = orgName;
  }

  setOrgId(orgId) {
    this.orgId = orgId;
  }

  setHeartRate(userCode, heartRate) {
    this.heartRate[userCode] = heartRate;
  }

  setMetrics(userCode, metrics) {
    this.metrics[userCode] = metrics;
  }

  setUserStats(userCode, stats) {
    this.userStats[userCode] = stats;
  }

  appendToHeartRate(userCode, heartRates, limit) {
    heartRates.forEach((x) => {
      this.heartRate[userCode].push(x);
    });
    if (this.heartRate[userCode].length > limit) {
      this.heartRate[userCode].sort((x, y) => {
        if (x.timestamp < y.timestamp) return -1;
        if (x.timestamp > y.timestamp) return 1;
        return 0;
      });

      this.heartRate[userCode] = this.heartRate[userCode].splice(
        this.heartRate[userCode].length - limit,
        limit
      );
    }
    this.lastHeartRateCount += 1;
  }

  appendToMetrics(userCode, metrics, limit) {
    const sorter = (x, y) => {
      if (x.timestamp < y.timestamp) return -1;
      if (x.timestamp > y.timestamp) return 1;
      return 0;
    };
    if (metrics.length === 0) {
      return;
    }

    metrics.sort(sorter);
    this.metrics[userCode].sort(sorter);
    const arr = [];
    this.metrics[userCode].forEach((x) => {
      if (new Date(x.timestamp) < new Date(metrics[0].timestamp)) {
        arr.push(x);
      }
    });

    this.metrics[userCode] = [...arr, ...metrics];

    if (this.metrics[userCode].length > limit) {
      this.metrics[userCode] = this.metrics[userCode].splice(
        this.metrics[userCode].length - limit,
        limit
      );
    }
    this.lastMetricsCount += 1;
  }

  clearSubscription() {
    this.subscriptions = [];
  }

  addSubscription(userCode) {
    if (this.subscriptions.indexOf(userCode) < 0) {
      this.subscriptions.push(userCode);

      subscribe(this.subscriptions);
    }
  }

  setStressLevel(userCode, green, yellow, red) {
    this.red[userCode] = red;
    this.yellow[userCode] = yellow;
    this.green[userCode] = green;
  }

  get redSdnn() {
    if (!this.users) {
      return "-";
    }
    let count = 0;
    this.users.forEach((user) => {
      if (this.red[user.user_id]) {
        count += 1;
      }
    });
    return count;
  }

  get greenSdnn() {
    if (!this.users) {
      return "-";
    }
    let count = 0;
    this.users.forEach((user) => {
      if (this.green[user.user_id]) {
        count += 1;
      }
    });
    return count;
  }

  get yellowSdnn() {
    if (!this.users) {
      return "-";
    }
    let count = 0;
    this.users.forEach((user) => {
      if (this.yellow[user.user_id]) {
        count += 1;
      }
    });
    return count;
  }

  get isActive() {
    const active = {};
    Object.keys(this.heartRate).forEach((userCode) => {
      const timeseries = this.heartRate[userCode];
      if (timeseries.length === 0) {
        active[userCode] = false;
      } else {
        const date = new Date(timeseries[timeseries.length - 1].timestamp);
        const diff = new Date().getTime() - date.getTime();
        if (diff < 60 * 1000) {
          active[userCode] = true;
        } else {
          active[userCode] = false;
        }
      }
    });
    return active;
  }

  get activeUsers() {
    if (!this.users) {
      return 0;
    }
    const activeMap = this.isActive;
    return this.users.filter((x) => activeMap[x.user_id]).length;
  }

  get totalUsers() {
    if (!this.users) {
      return 0;
    }
    return this.users.length;
  }

  setFilteredHeartRates(filteredHeartRates) {
    this.filteredHeartRates = filteredHeartRates;
  }

  setFilteredRRIntervals(filteredRRIntervals) {
    this.filteredRRIntervals = filteredRRIntervals;
  }

  setFilteredSdnn(filteredSdnn) {
    this.filteredSdnn = filteredSdnn;
  }
  setFilteredStatsData(filteredStatsData) {
    this.filteredStatsData = filteredStatsData;
  }
  setFilteredStatus(filteredStatus) {
    this.filteredStatus = filteredStatus;
  }
  setIsRealtime(isRealtime) {
    this.isRealtime = isRealtime;
  }

  setFilterStartDate(filterStartDate) {
    this.filterStartDate = filterStartDate;
  }
  setFilterEndDate(filterEndDate) {
    this.filterEndDate = filterEndDate;
  }

  setAdminMode(adminMode) {
    this.adminMode = adminMode;
  }

  constructor() {
    makeAutoObservable(this, {
      setUsers: action,
      addSubscription: action,
      isActive: computed,
      setOrgName: action,
      setOrgId: action,
      setHeartRate: action,
      setMetrics: action,
      setUserStats: action,
      appendToHeartRate: action,
      appendToMetrics: action,
      clearSubscription: action,
      meanSdnns: computed,
      greenSdnn: computed,
      redSdnn: computed,
      yellowSdnn: computed,
      setFilteredHeartRates: action,
      setFilteredSdnn: action,
      setFilteredStatsData: action,
      setIsRealtime: action,
      setStressLevel: action,
      setFilterStartDate: action,
      setFilterEndDate: action,
      setFilteredRRIntervals: action,
      setFilteredStatus: action,
      setAdminMode: action,
    });
  }
}
